import { useEffect } from "react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Learn how Hadassah Homes collects, uses, and protects your personal information. Read our full privacy policy.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://hadassahhomesinc.com/privacy-policy";
  }, []);

  return (
    <SiteLayout>
      <PageHero eyebrow="Legal" title="Privacy Policy" subtitle="How We Collect, Use, and Protect Your Information" compact />

      {/* SECTION 2 — INTRO */}
      <ContentSection>
        <div className="mx-auto max-w-prose space-y-6 text-lg md:text-xl leading-relaxed text-muted-foreground">
          <p>At Hadassah Homes, your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your personal information when you visit our website or use our services.</p>
          <p>By using our website, you agree to the practices described in this policy.</p>
        </div>
      </ContentSection>

      {/* SECTION 3 — INFORMATION WE COLLECT */}
      <ContentSection variant="dark">
        <SectionHeader title="Information We Collect" align="left" />
        <div className="mx-auto max-w-prose space-y-8">
          <div className="space-y-4">
            <h3 className="font-headline text-xl md:text-2xl text-foreground">Personal Information</h3>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We may collect the following personal information when you interact with our website or services:</p>
            <ul className="space-y-2 text-muted-foreground text-base md:text-lg leading-relaxed list-disc pl-6">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Mailing address</li>
              <li>Information submitted through contact forms or consultation requests</li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="font-headline text-xl md:text-2xl text-foreground">Usage Information</h3>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We may also collect non-personal information automatically, including:</p>
            <ul className="space-y-2 text-muted-foreground text-base md:text-lg leading-relaxed list-disc pl-6">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent on our site</li>
              <li>Referring website or source</li>
            </ul>
          </div>
        </div>
      </ContentSection>

      {/* SECTION 4 — HOW WE USE YOUR INFORMATION */}
      <ContentSection>
        <SectionHeader title="How We Use Your Information" align="left" />
        <div className="mx-auto max-w-prose space-y-4">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We use the information we collect to:</p>
          <ul className="space-y-2 text-muted-foreground text-base md:text-lg leading-relaxed list-disc pl-6">
            <li>Respond to inquiries and consultation requests</li>
            <li>Provide real estate services and support</li>
            <li>Send relevant updates, market insights, or promotional content (with your consent)</li>
            <li>Improve our website and user experience</li>
            <li>Comply with legal obligations</li>
          </ul>
        </div>
      </ContentSection>

      {/* SECTION 5 — SHARING YOUR INFORMATION */}
      <ContentSection variant="dark">
        <SectionHeader title="Sharing Your Information" align="left" />
        <div className="mx-auto max-w-prose space-y-4">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We do not sell your personal information. We may share your information with:</p>
          <ul className="space-y-2 text-muted-foreground text-base md:text-lg leading-relaxed list-disc pl-6">
            <li>Trusted service providers who assist in operating our website or delivering services</li>
            <li>Legal or regulatory authorities when required by law</li>
            <li>Third-party partners involved in a real estate transaction (with your knowledge)</li>
          </ul>
        </div>
      </ContentSection>

      {/* SECTION 6 — COOKIES AND TRACKING */}
      <ContentSection>
        <SectionHeader title="Cookies and Tracking Technologies" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Our website may use cookies and similar technologies to enhance your browsing experience. You can manage your cookie preferences through your browser settings.</p>
        </div>
      </ContentSection>

      {/* SECTION 7 — DATA SECURITY */}
      <ContentSection variant="dark">
        <SectionHeader title="Data Security" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We take reasonable measures to protect your personal information from unauthorized access, use, or disclosure. However, no method of transmission over the internet is 100% secure.</p>
        </div>
      </ContentSection>

      {/* SECTION 8 — YOUR RIGHTS */}
      <ContentSection>
        <SectionHeader title="Your Rights" align="left" />
        <div className="mx-auto max-w-prose space-y-4">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">You have the right to:</p>
          <ul className="space-y-2 text-muted-foreground text-base md:text-lg leading-relaxed list-disc pl-6">
            <li>Access the personal information we hold about you</li>
            <li>Request correction or deletion of your information</li>
            <li>Opt out of marketing communications at any time</li>
          </ul>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">To exercise any of these rights, please contact us using the information below.</p>
        </div>
      </ContentSection>

      {/* SECTION 9 — THIRD-PARTY LINKS */}
      <ContentSection variant="dark">
        <SectionHeader title="Third-Party Links" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Our website may contain links to third-party websites. We are not responsible for the privacy practices of those sites and encourage you to review their policies.</p>
        </div>
      </ContentSection>

      {/* SECTION 10 — CHILDREN'S PRIVACY */}
      <ContentSection>
        <SectionHeader title="Children's Privacy" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Our website and services are not intended for individuals under the age of 13. We do not knowingly collect personal information from children.</p>
        </div>
      </ContentSection>

      {/* SECTION 11 — CHANGES TO THIS POLICY */}
      <ContentSection variant="dark">
        <SectionHeader title="Changes to This Policy" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.</p>
        </div>
      </ContentSection>

      {/* SECTION 12 — CONTACT US */}
      <ContentSection>
        <SectionHeader title="Contact Us" align="left" />
        <div className="mx-auto max-w-prose space-y-4">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">If you have any questions about this Privacy Policy or how we handle your information, please contact us:</p>
          <div className="space-y-2 text-foreground text-base md:text-lg">
            <p>Email: <a href="mailto:Esther@hadassahhomesinc.com" className="text-accent hover:text-accent/80 transition-colors">Esther@hadassahhomesinc.com</a></p>
            <p>Phone: <a href="tel:+17816323936" className="text-accent hover:text-accent/80 transition-colors">(781) 632-3936</a></p>
          </div>
        </div>
      </ContentSection>
    </SiteLayout>
  );
};

export default PrivacyPolicy;
